import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import type { CoverageItem } from '@/client/types/media';
import {
  MEDIA_CLASS_BADGE,
  MEDIA_SENTIMENT_BADGE,
  MEDIA_STATUS_BADGE,
  PLATFORM_LABEL,
  SOCIAL_FORMAT_LABEL,
  publicMetrics,
} from '@/client/types/media';
import { formatCompact, sourceLabel } from '../fixtures';
import { useApiTrackedKeywords } from '../coverage-api';
import { useTriageCoverageMutation } from '@/client/api/marketing/radar';
import { getApiErrorMessage } from '@/client/utils/errors';
import { SocialPlatformPreview } from './SocialPlatformPreview';
import { matchingKeywords } from '../keyword-match';

interface CoverageDetailDrawerProps {
  item: CoverageItem | null;
  open: boolean;
  onClose: () => void;
}

function Badge({ className, children }: { className: string; children: string }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${className}`}>
      {children}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900">{children}</dd>
    </div>
  );
}

export function CoverageDetailDrawer({ item, open, onClose }: CoverageDetailDrawerProps) {
  const { keywords: trackedKeywords } = useApiTrackedKeywords();
  const triage = useTriageCoverageMutation();

  const setStatus = (status: CoverageItem['status']) => {
    if (!item) return;
    triage.mutate(
      { coverage_id: item.id, status },
      {
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not triage this item')),
      },
    );
    toast.success(`Marked as ${status} (mock)`);
  };

  if (!item) return null;

  const isSocial = item.channel === 'social' && !!item.social;
  const matchedKws = matchingKeywords(
    item,
    trackedKeywords.filter(
      (k) => k.enabled && k.channels.includes(item.channel),
    ),
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader className="space-y-2 text-left">
          <SheetTitle className="pr-6 text-lg leading-snug">{item.title}</SheetTitle>
          <SheetDescription>{item.summary}</SheetDescription>
          <div className="flex flex-wrap gap-2 pt-1">
            <Badge className={MEDIA_STATUS_BADGE[item.status]}>{item.status}</Badge>
            <Badge className={MEDIA_SENTIMENT_BADGE[item.sentiment]}>{item.sentiment}</Badge>
            <Badge className={MEDIA_CLASS_BADGE[item.mediaClass]}>{item.mediaClass}</Badge>
            <Badge className="bg-gray-100 text-gray-700">{item.kind}</Badge>
            {item.platform && (
              <Badge className="bg-gray-900 text-white">{PLATFORM_LABEL[item.platform]}</Badge>
            )}
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {isSocial && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Platform preview
              </h3>
              <SocialPlatformPreview item={item} />
            </section>
          )}

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Evidence</h3>
            {!isSocial && item.bodyExcerpt && (
              <p className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700">
                “{item.bodyExcerpt}”
              </p>
            )}
            <dl className="space-y-2">
              <Row label="Source">{sourceLabel(item)}</Row>
              <Row label="Author">{item.author ?? '—'}</Row>
              {item.social && (
                <>
                  <Row label="Format">{SOCIAL_FORMAT_LABEL[item.social.format]}</Row>
                  <Row label="Followers">
                    {formatCompact(item.social.authorFollowers)}
                  </Row>
                  <Row label="Hashtags">
                    {item.social.hashtags?.map((h) => `#${h.replace(/^#/, '')}`).join(' ') || '—'}
                  </Row>
                  <Row label="Mentions">{item.social.mentions?.join(' ') || '—'}</Row>
                  <Row label="Location">{item.social.location ?? '—'}</Row>
                </>
              )}
              <Row label="Published">{new Date(item.publishedAt).toLocaleString()}</Row>
              <Row label="URL">
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                  >
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  '—'
                )}
              </Row>
            </dl>
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Tracking
            </h3>
            {matchedKws.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {matchedKws.map((kw) => (
                  <span
                    key={kw.id}
                    className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-800"
                  >
                    {kw.label}
                  </span>
                ))}
              </div>
            )}
            <dl className="space-y-2">
              <Row label="Urgency">{item.urgencyScore ?? '—'}</Row>
              <Row label="Basis">{item.measurementBasis ?? '—'}</Row>
              {(() => {
                const pub = publicMetrics(item);
                const rich = item.metrics;
                const showRich =
                  item.metricsPublic != null &&
                  (rich.impressions != null || rich.reach != null) &&
                  (rich.impressions !== pub.impressions || rich.reach !== pub.reach);
                return (
                  <>
                    <Row label="Reach (public)">{formatCompact(pub.reach ?? pub.views)}</Row>
                    <Row label="Eng. rate (public)">
                      {pub.engagementRate != null
                        ? `${(pub.engagementRate * 100).toFixed(1)}%`
                        : '—'}
                    </Row>
                    <Row label="Likes / comments">
                      {formatCompact(pub.likes)} / {formatCompact(pub.comments)}
                    </Row>
                    <Row label="Shares / saves">
                      {formatCompact(pub.shares)} / {formatCompact(pub.saves)}
                    </Row>
                    <Row label="PR value">
                      {pub.prValue != null ? `$${formatCompact(pub.prValue)}` : '—'}
                    </Row>
                    {showRich && (
                      <>
                        <Row label="Impressions (connected)">
                          {formatCompact(rich.impressions ?? rich.views)}
                        </Row>
                        <Row label="Reach (connected)">{formatCompact(rich.reach)}</Row>
                      </>
                    )}
                  </>
                );
              })()}
              {item.social && (
                <>
                  <Row label="Tracked since">
                    {item.social.trackedSince
                      ? new Date(item.social.trackedSince).toLocaleString()
                      : '—'}
                  </Row>
                  <Row label="Last eng.">
                    {item.social.lastEngagementAt
                      ? new Date(item.social.lastEngagementAt).toLocaleString()
                      : '—'}
                  </Row>
                </>
              )}
              <Row label="Rules">
                {item.matchedRuleIds.length ? item.matchedRuleIds.join(', ') : '—'}
              </Row>
            </dl>
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Context</h3>
            <dl className="space-y-2">
              <Row label="Links">
                {item.links.length === 0 ? (
                  '—'
                ) : (
                  <ul className="space-y-1">
                    {item.links.map((l) => (
                      <li key={`${l.type}-${l.id}`}>
                        <span className="text-xs uppercase text-gray-400">{l.type}</span> {l.label}
                      </li>
                    ))}
                  </ul>
                )}
              </Row>
              <Row label="Tags">{item.tags.join(', ') || '—'}</Row>
              <Row label="Assignee">{item.assigneeName ?? 'Unassigned'}</Row>
              <Row label="Note">{item.internalNote ?? '—'}</Row>
            </dl>
          </section>

          {item.status === 'flagged' || item.sentiment === 'negative' ? (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">Playbook: reputation risk</p>
              <p className="mt-1 text-amber-800">
                Assign Comms, draft public response with Trust & Safety, and add to today’s crisis brief.
              </p>
            </section>
          ) : null}

          <section className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
            <Button size="sm" variant="default" onClick={() => setStatus('reviewed')}>
              Mark reviewed
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setStatus('flagged')}>
              Flag
            </Button>
            <Button size="sm" variant="outline" onClick={() => setStatus('archived')}>
              Archive
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => toast.message('Added to brief (mock)')}
            >
              Add to brief
            </Button>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
